import invariant from 'tiny-invariant'
import { v4 as uuidv4 } from 'uuid'

import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import config from 'constants/config'
import { arrayToObject } from 'lib/array/arrayUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import { ICONS } from 'lib/offer/getPropertyIcon'

/* ***** */
/* Utils */
/* ***** */

function mapOptional<T, U>(value: T | null | undefined, map: (value: T) => U): U | undefined {
  return value ? map(value) : undefined
}

/* *********** */
/* Sub-mappers */
/* *********** */

type ServerAmenity = definitions['Amenity']
type ServerAmenityGroup = definitions['AmenityGroup']
type ServerContent = definitions['Content']
type ServerDuration = definitions['Duration']
type ServerIcon = definitions['Icon']
type ServerImage = definitions['Image']
type ServerOccupancy = definitions['Occupancy']
type ServerPopularFacility = definitions['PopularFacility']
type ServerPropertyLocation = definitions['PropertyLocation']
type ServerPropertyReviews = definitions['PropertyReviews']
type ServerRoomInclusion = definitions['RoomInclusion']
type ServerRoomPromotion = definitions['RoomPromotion']
type ServerStats = definitions['Stats']
type ServerStructuredFinePrint = definitions['StructuredFinePrint']

function mapAmenityGroup({ name, values }: ServerAmenityGroup): App.Property.AmenityGroup {
  return {
    name,
    items: values.map(mapAmenityItem),
  }
}

function mapAmenityItem({ name }: ServerAmenity): App.Property.AmenityItem {
  return { id: name, name, iconName: undefined }
}

function mapCapacities({
  adults,
  teenagers,
  children,
  infants,
}: ServerOccupancy): App.RoomCapacity {
  return { adults, teenagers, children, infants }
}

function mapDurationOption({
  duration,
  hasBonusInclusions,
  hasTactical,
  promotion,
}: ServerDuration): App.Property.DurationOption {
  return {
    duration,
    hasBonusInclusions,
    hasTactical,
    promotion: promotion ? {
      travelFromIsoDate: promotion.travelPeriod.from,
      travelToIsoDate: promotion.travelPeriod.to,
    } : undefined,
  }
}

function mapIconName(icon: ServerIcon): App.Property.IconName | undefined {
  return ICONS[icon] ? icon : undefined
}

function mapImage({ id, title }: ServerImage): App.Image {
  return { id, title: title ?? undefined }
}

function mapLocation({
  coordinates,
  timezone,
  timezoneOffset,
  address,
  streetAddress,
  city,
  region,
  countryName,
  countryCode,
  continentCode,
  placeId,
}: ServerPropertyLocation, title: ServerPropertyContent['locationTitle']): App.Property.Location {
  return {
    timezone,
    timezoneOffset: timezoneOffset ?? undefined,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    title: title ?? undefined,
    formattedAddress: address,
    streetAddress: streetAddress ?? undefined,
    city: city ?? undefined,
    region: region ?? undefined,
    countryName: countryName ?? undefined,
    countryCode: countryCode ?? undefined,
    continentCode: continentCode ?? undefined,
    placeId: placeId ?? undefined,
  }
}

function mapMarkdown({ type, value }: ServerContent): string {
  // We don't have a static assertion, as `type` is allowed to take other values. However, in
  // practice, accommodation service won't be serving `'text' | 'html'` for a while.
  invariant(type === 'markdown', 'Expected Markdown content type')
  return value
}

function mapPopularFacility({
  code,
  name,
  icon,
}: ServerPopularFacility): App.Property.AmenityItem {
  return {
    id: code,
    name,
    iconName: mapIconName(icon),
  }
}

function mapRating({ rating, count, source, ratingBreakdown }: ServerPropertyReviews): App.OfferRating {
  return {
    score: rating,
    reviewsTotal: count,
    reviewsSource: source,
    reviewsRatingBreakdown: ratingBreakdown,
  }
}

function mapRoomInclusion({
  // code, // This isn't guaranteed to be unique.
  // name, // This shouldn't be displayed.
  text,
  icon,
  // unit, // This cannot be mapped to `App.OfferInclusion`.
  minLos,
  maxLos,
}: ServerRoomInclusion, type: 'standard' | 'bonus' | 'luxPlus'): App.OfferInclusion {
  return {
    id: uuidv4(),
    description: text,
    symbol: determineOfferFeatureSymbol(icon || text),
    isBonus: type === 'bonus',
    isLuxPlusBonus: type === 'luxPlus',
    minDuration: minLos ?? undefined,
    maxDuration: maxLos ?? undefined,
  }
}

function mapRoomInclusions(
  type: 'standard' | 'bonus' | 'luxPlus',
): (serverRoomInclusions: Array<ServerRoomInclusion>) => Array<App.OfferInclusion> {
  return (serverRoomInclusions) =>
    serverRoomInclusions.map(serverRoomInclusion => mapRoomInclusion(serverRoomInclusion, type))
}

function mapRoomPromotion({
  name,
  travelPeriod: {
    from: travelFromIsoDate,
    to: travelToIsoDate,
  },
  minimumLos,
}: ServerRoomPromotion): App.Property.Promotion {
  return {
    name,
    travelFromIsoDate,
    travelToIsoDate,
    minimumLos,
  }
}

function mapStats({ purchaseCount, viewCount }: ServerStats): App.OfferStats {
  return { booked: purchaseCount, views: viewCount }
}

function mapStructuredFinePrint({
  checkIn,
  checkOut,
  fees,
  // general, // This is unused.
  pets,
  policies,
  safety,
}: ServerStructuredFinePrint): App.BedbankPropertyFinePrint {
  const structuredFinePrint: App.BedbankPropertyFinePrint = {
    pets,
    safety,
  }
  if (checkIn.beginTime || checkIn.endTime || checkIn.instructions || checkIn.specialInstructions) {
    structuredFinePrint.checkIn = {
      beginTime: checkIn.beginTime,
      endTime: checkIn.endTime,
      instructions: mapOptional(checkIn.instructions, mapMarkdown),
      specialInstructions: mapOptional(checkIn.specialInstructions, mapMarkdown),
    }
  }
  if (checkOut.time) {
    structuredFinePrint.checkOut = {
      time: checkOut.time,
    }
  }
  if (fees?.mandatory || fees?.optional) {
    structuredFinePrint.fees = {
      mandatory: mapOptional(fees.mandatory, mapMarkdown),
      optional: mapOptional(fees.optional, mapMarkdown),
    }
  }
  if (policies?.knowBeforeYouGo) {
    structuredFinePrint.policies = {
      knowBeforeYouGo: mapMarkdown(policies.knowBeforeYouGo),
    }
  }
  return structuredFinePrint
}

/* ************ */
/* Main mappers */
/* ************ */

type ServerPropertyContent = definitions['PropertyContent']
type ServerRoomContent = definitions['RoomContent']

interface MapRoomOptions {
  defaultOrder: number
  previewMode: boolean
}

function mapRoom({
  id,
  name,
  copy: {
    description,
    roomPolicy,
  },
  images,
  amenityGroups,
  popularAmenities,
  roomSize,
  numberOfBedrooms,
  numberOfBathrooms,
  bedGroupDescriptions,
  occupancies,
  stats,
  promotion,
  inclusions,
  bonusInclusions,
  luxPlusInclusions,
  // ageCategories, // This is a bit impractical to use.
  isMysteryRoom,
  shouldHideUnlessHasRates,
}: ServerRoomContent, {
  defaultOrder,
  previewMode,
}: MapRoomOptions): App.Property.Room {
  return {
    /* Identifiers */
    id,
    /* Status flags */
    isMysteryRoom,
    shouldHideUnlessHasRates: !previewMode && shouldHideUnlessHasRates,
    /* Configuration options */
    capacities: occupancies.map(mapCapacities),
    /* Content */
    name,
    images: images.map(mapImage),
    description: mapOptional(description, mapMarkdown),
    bedrooms: numberOfBedrooms ?? undefined,
    bedGroupDescriptions,
    bathrooms: numberOfBathrooms ?? undefined,
    size: roomSize ?? undefined,
    popularAmenities: popularAmenities.map(mapPopularFacility),
    amenities: amenityGroups.map(mapAmenityGroup),
    policy: mapOptional(roomPolicy, mapMarkdown),
    /* Inclusions */
    promotion: mapOptional(promotion, mapRoomPromotion),
    inclusions: mapOptional(inclusions, mapRoomInclusions('standard')) ?? [],
    bonusInclusions: mapOptional(bonusInclusions, mapRoomInclusions('bonus')) ?? [],
    luxPlusInclusions: mapOptional(luxPlusInclusions, mapRoomInclusions('luxPlus')) ?? [],
    /* Metadata */
    defaultOrder,
    stats: mapStats(stats),
  }
}

// NB: This mapper (as well as transitive mappers and related Customer Portal types) was last
// cross-checked against `contract-svc-accommodation` v6.10.0.
export default function mapProperty({
  id,
  name,
  copy: {
    description,
    additionalDescription,
    highlights,
    facilities,
    locationDescription,
    tagline,
    // metaDescription, // This is unused; Customer Portal generates a generic meta description.
    finePrint,
    taxesAndFees,
  },
  // type, // This is only used on the checkout page, not the offer page.
  logo,
  slug,
  rooms,
  images,
  reviews,
  location,
  locationTitle,
  amenityGroups,
  popularFacilities,
  flights: {
    destinationAirport,
    // cacheDisabled, // This is irrelevant for SPP; we always use the single cheapest flight endpoint.
    enabled: areFlightsEnabled,
    maxArrivalTime,
    minReturningDepartureTime,
    bundledWithFlightsOnly,
    // warning, // This is only used on the checkout page, not the offer page.
  },
  structuredFinePrint,
  stats,
  tags: {
    // themes, // This is unused (for now).
    // urgency, // This is unused by SPP and bedbank offer pages (for now).
    location: locationTags,
    holidayTypes: holidayTypeTags,
    campaigns: campaignTags,
  },
  promotion,
  durations,
  marketedLos,
  walledGarden,
  // saleUnit, // This is unused (for now).
  isAgentHubExclusive,
  hasContentVariant,
}: ServerPropertyContent, previewMode: boolean): App.Property.Property {
  return {
    /* Identifiers */
    id,
    slug,
    /* Configuration options */
    walledGarden,
    marketedLos,
    areFlightsEnabled: config.FLIGHT_ENABLED && (
      // NB: Agent Hub sets `FLIGHT_ENABLED`, but this was only ever intended for offers bundled
      // with Maldives charter flights. It matches `canHotelOfferFlightsBeEnabled`'s behaviour.
      !config.ENABLE_ONLY_HOTEL_OFFER_FLIGHTS_FOR_MALDIVES_CHARTS ||
      campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
    ) && areFlightsEnabled,
    mustBundleWithFlights: !!bundledWithFlightsOnly,
    airportCode: destinationAirport ?? undefined,
    latestDepartingFlightArrivalTime: maxArrivalTime ?? undefined,
    earliestReturningFlightDepartureTime: minReturningDepartureTime ?? undefined,
    isAgentHubExclusive: config.agentHub.isEnabled && !!isAgentHubExclusive,
    rooms: arrayToObject(
      rooms,
      ({ id }) => id,
      (room, index) => mapRoom(room, {
        defaultOrder: index,
        previewMode,
      }),
    ),
    /* Content */
    name,
    headline: tagline ?? undefined,
    logo: mapOptional(logo, mapImage),
    images: images.map(mapImage),
    description: mapOptional(description, mapMarkdown),
    additionalDescription: mapOptional(additionalDescription, mapMarkdown),
    location: mapLocation(location, locationTitle),
    locationDescription: mapOptional(locationDescription, mapMarkdown),
    highlights: mapOptional(highlights, mapMarkdown),
    popularFacilities: popularFacilities.map(mapPopularFacility),
    facilities: mapOptional(facilities, mapMarkdown),
    amenities: amenityGroups.map(mapAmenityGroup),
    finePrint: mapOptional(finePrint, mapMarkdown),
    structuredFinePrint: mapOptional(structuredFinePrint, mapStructuredFinePrint),
    taxesAndFeesContent: mapOptional(taxesAndFees, mapMarkdown),
    /* Marketed rates */
    promotionEndIsoDateTime: promotion?.activePeriod.to,
    durationOptions: durations.map(mapDurationOption),
    /* Metadata */
    rating: mapOptional(reviews, mapRating),
    stats: mapStats(stats),
    campaignTags,
    holidayTypeTags,
    locationTags,
    hasContentVariant: !!hasContentVariant,
  }
}
